/**
 * Générateur — Savoir que le périmètre du disque est proportionnel à son diamètre
 * Attendu ID : 639 | Niveau : 6ème | Domaine : Grandeurs et mesures — Les longueurs
 *
 * Types générés : qcm_unique, texte, tableau_vf, texte_trou_drag
 * Labels de référence : Constater expérimentalement la proportionnalité périmètre/diamètre
 * Variantes estimées : ~500+
 */

import {
    randomChoice, randomChoiceMultiple, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 639;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PROP = 'Constater expérimentalement la proportionnalité périmètre/diamètre';

// ─── Pools de données ───
// Chaque objet a un genre ('m' ou 'f') et une liste de diamètres réalistes (cm)
const OBJETS = [
    { nom: 'Assiette',          genre: 'f', diams: [20, 22, 24, 26, 27] },
    { nom: 'CD',                genre: 'm', diams: [12] },
    { nom: 'Boîte de conserve', genre: 'f', diams: [6.5, 7, 7.5, 8, 8.5, 9] },
    { nom: 'Pot de yaourt',     genre: 'm', diams: [5, 5.5, 6, 6.5, 7] },
    { nom: 'Rouleau de scotch', genre: 'm', diams: [4, 4.5, 5, 5.5, 6] },
    { nom: 'Couvercle de bocal',genre: 'm', diams: [6, 6.5, 7, 7.5, 8, 8.5, 9] },
    { nom: 'Verre',             genre: 'm', diams: [6, 6.5, 7, 7.5, 8] },
    { nom: 'Canette',           genre: 'f', diams: [6, 6.5, 7] },
    { nom: 'Horloge',           genre: 'f', diams: [18, 20, 22, 24, 25] },
    { nom: 'Pizza',             genre: 'f', diams: [25, 27, 28, 30, 32] },
    { nom: 'Frisbee',           genre: 'm', diams: [22, 24, 25, 26, 28] },
    { nom: 'Soucoupe',          genre: 'f', diams: [12, 13, 14, 15, 16] },
    { nom: 'Galet',             genre: 'm', diams: [4, 5, 6, 7] },
    { nom: 'Médaille',          genre: 'f', diams: [3, 4, 5] },
    { nom: 'Bougie ronde',      genre: 'f', diams: [5, 6, 7, 8] },
];

// Simule une mesure expérimentale avec petite imprécision
const mesurePerimetre = (d) => {
    const pExact = Math.PI * d;
    // Arrondi au dixième (simule mesure réelle)
    return Math.round(pExact * 10) / 10;
};

const quotientArrondi = (p, d) => Math.round((p / d) * 100) / 100;

// ─── Pool d'affirmations V/F ───
// L'irrationalité de π (décimales infinies, « pas un décimal », « pas une fraction »)
// est une notion de 3e, HORS de l'attendu 6e « périmètre proportionnel au diamètre ».
// Les 3 affirmations correspondantes sont remplacées, à effectif constant, par des
// affirmations de proportionnalité de niveau 6e.
const AFFIRMATIONS_VRAIES = [
    'Pour tous les disques, le rapport P ÷ D est le même',
    '3,14 est une valeur approchée de π',
    'Le nombre π est le rapport entre le périmètre et le diamètre de tout disque',
    'Si le diamètre double, le périmètre double aussi',
];

const AFFIRMATIONS_FAUSSES = [
    'Le nombre π vaut exactement 3,14',
    'Le périmètre est proportionnel au rayon avec le coefficient π',
    'Si le diamètre est multiplié par 3, le périmètre est multiplié par 6',
    'Le rapport P ÷ D est différent selon la taille du disque',
    'π vaut exactement 3',
];

// ─── Générateurs dynamiques de phrases à trous ───
// Chaque entrée est une fonction qui renvoie une phrase + distracteurs, avec paramètres aléatoires.
const PHRASES_TROU_GEN = [
    // 1. Rapport constant (3 variations : mots synonymes)
    () => {
        const v = randomChoice([
            { sujet: 'le rapport', sub: 'un nombre {{constant}}', distr: 'variable' },
            { sujet: 'le quotient', sub: 'une valeur {{constante}}', distr: 'différente' },
            { sujet: 'le rapport', sub: 'une {{constante}}', distr: 'inconnue' },
        ]);
        return {
            phrase: `Pour tous les disques, ${v.sujet} entre le {{périmètre}} et le {{diamètre}} est ${v.sub} notée {{π}}.`,
            distracteurs: ['rayon', 'aire', v.distr],
        };
    },
    // 2. Effet d'un agrandissement (remplace « π irrationnel », notion de 3e —
    //    retouche 2026-07-28, même arbitrage que le V/F corrigé : l'attendu 6e
    //    est la PROPORTIONNALITÉ périmètre/diamètre). Aucun assignement
    //    alternatif vrai : « égal » est faux au trou 1, 2k et k² faux au trou 2.
    () => {
        const k = randomChoice([2, 3, 5, 10]);
        return {
            phrase: `Le périmètre d'un disque est {{proportionnel}} à son diamètre : si le diamètre est multiplié par ${k}, le périmètre est multiplié par {{${k}}}.`,
            distracteurs: ['égal', String(k * 2), String(k * k)],
        };
    },
    // 3. Arrondi de π (5 variations : précision × place). Valeur EN CLAIR
    //    (revue 2026-07-31) : quand la valeur était un trou, les distracteurs
    //    permettaient d'assembler une AUTRE paire (valeur, place) vraie —
    //    « 3,14 est l'arrondi au centième de π », comptée fausse — dans 3
    //    variantes sur 5. Valeur fixée + distracteurs = PLACES uniquement
    //    (jamais de nombre : « 3 est l'arrondi à l'unité du nombre 3,14 »
    //    serait vrai) → une seule phrase vraie possible, par construction.
    () => {
        const v = randomChoice([
            { val: '3', place: 'unité',        d: ['dixième', 'centième'] },
            { val: '3,1', place: 'dixième',    d: ['unité', 'centième'] },
            { val: '3,14', place: 'centième',  d: ['dixième', 'millième'] },
            { val: '3,142', place: 'millième', d: ['centième', 'dix-millième'] },
            { val: '3,1416', place: 'dix-millième', d: ['centième', 'millième'] },
        ]);
        return {
            phrase: `${v.val} est l'arrondi au {{${v.place}}} du nombre {{π}}.`,
            distracteurs: v.d,
        };
    },
    // 4. Formule P = π × D — D en CLAIR (revue 2026-07-31) : {{π}} × {{D}}
    //    formait une paire COMMUTATIVE (« P = D × π » vrai, compté faux par le
    //    correcteur positionnel), et la fin de phrase « à partir de son
    //    diamètre » soufflait de toute façon la réponse du trou D.
    () => {
        return {
            phrase: `La formule P = {{π}} × D permet de calculer le {{périmètre}} d'un disque à partir de son diamètre.`,
            distracteurs: ['R', 'rayon', randomChoice(['aire', 'surface', 'longueur'])],
        };
    },
    // 5. Périmètre depuis le RAYON (remplace « décimales infinies », notion de
    //    3e — retouche 2026-07-28) : c'est le pendant du piège V/F « coefficient
    //    π avec le rayon » (la vérité : ×2×π). Aucun assignement alternatif
    //    vrai : diamètre/côté au trou 1 et 3 au trou 2 rendent la phrase fausse.
    () => {
        return {
            phrase: `Pour calculer le périmètre d'un disque à partir de son {{rayon}}, on multiplie par {{2}} × π.`,
            distracteurs: ['diamètre', '3', 'côté'],
        };
    },
    // 6. Proportionnalité : k fois le diamètre (3 coefficients)
    () => {
        const v = randomChoice([
            { verb: 'double', adj: 'doublé', distr: 'inchangé' },
            { verb: 'triple', adj: 'triplé', distr: 'inchangé' },
            { verb: 'quadruple', adj: 'quadruplé', distr: 'réduit' },
        ]);
        return {
            // « rayon » RETIRÉ des distracteurs (revue 2026-07-31) : « si on
            // double le rayon, le périmètre est aussi doublé » est VRAI
            // (P = 2πR) — l'élève qui le posait faisait une phrase juste
            // comptée fausse. « côté » à la place (un disque n'a pas de côté —
            // même distracteur vocabulaire que le gabarit 5, validé 28/07).
            phrase: `Si on ${v.verb} le {{diamètre}} d'un disque, alors son {{périmètre}} est aussi {{${v.adj}}}.`,
            distracteurs: ['côté', 'aire', v.distr],
        };
    },
    // 7. Quotient P/D valeur approchée. Distracteurs numériques = VIRGULE
    //    DÉCALÉE (revue 2026-07-31) : les anciens (« 3 », « 3,14 » face à
    //    « 3,142 ») étaient défendables avec « environ » — l'élève assemblait
    //    une phrase vraie comptée fausse. « environ 31,4 » est indéfendable,
    //    et le choisir révèle une vraie erreur de division (virgule glissée).
    () => {
        const v = randomChoice([
            { val: '3,14', d: ['31,4', 'exacte', 'D'] },
            { val: '3,1', d: ['31', 'exacte', 'P'] },
            { val: '3,142', d: ['31,42', 'exacte', 'D'] },
        ]);
        return {
            phrase: `Le quotient P ÷ D vaut environ {{${v.val}}} ; c'est une valeur {{approchée}} de {{π}}.`,
            distracteurs: v.d,
        };
    },
    // 8. Diamètre = 2 × rayon
    () => {
        const v = randomChoice([
            { mot1: 'diamètre', nbr: '2', mot3: 'rayon', distr: '3' },
            { mot1: 'rayon',    nbr: '2', mot3: 'diamètre', distr: '4', verbe: 'petit' },
        ]);
        if (v.verbe) {
            return {
                phrase: `Dans un disque, le {{${v.mot1}}} est ${v.nbr} fois plus ${v.verbe} que le {{${v.mot3}}}.`,
                distracteurs: [v.distr, 'périmètre', 'aire'],
            };
        }
        return {
            phrase: `Dans un disque, le {{${v.mot1}}} est {{${v.nbr}}} fois plus grand que le {{${v.mot3}}}.`,
            distracteurs: [v.distr, 'périmètre', 'aire'],
        };
    },
    // 9. Proportionnalité P ↔ D
    () => {
        const v = randomChoice([
            { mot1: 'périmètre', mot2: 'proportionnel', mot3: 'π', d: ['rayon', 'inversement', '3'] },
            { mot1: 'périmètre', mot2: 'proportionnel', mot3: 'π', d: ['aire', 'égal', '2'] },
        ]);
        return {
            phrase: `Le {{${v.mot1}}} d'un disque est {{${v.mot2}}} à son diamètre, avec un coefficient égal à {{${v.mot3}}}.`,
            distracteurs: v.d,
        };
    },
    // 10. Encadrement de π — « entiers consécutifs » (revue 2026-07-31) : sans
    //     ce verrou, « entre 2 et 4 », « entre 3 et 5 » et « entre 2 et 5 »
    //     étaient VRAIS avec les distracteurs — comptés faux. Les paires
    //     consécutives restantes (2-3, 4-5) sont fausses mathématiquement.
    //     « 3,1 » remplacé par « 3,41 » (inversion de chiffres) : « environ
    //     3,1 » était défendable.
    () => {
        return {
            phrase: `Le nombre π est compris entre les deux entiers consécutifs {{3}} et {{4}} ; il vaut environ {{3,14}}.`,
            distracteurs: ['2', '5', '3,41'],
        };
    },
    // 11. Disque / Cercle
    () => {
        const mot = randomChoice(['disque', 'cercle']);
        return {
            phrase: `Pour calculer le {{périmètre}} d'un ${mot}, on {{multiplie}} son diamètre par {{π}}.`,
            distracteurs: ['rayon', 'additionne', '3'],
        };
    },
    // 12. Rayon = moitié du diamètre
    () => {
        return {
            phrase: `Le {{rayon}} d'un disque est la {{moitié}} de son diamètre.`,
            distracteurs: ['périmètre', 'double', 'aire'],
        };
    },
];

// ─── Générateurs par type ───

const genPropQcm = () => {
    // Revue 2026-07-31 : quotient affiché au MILLIÈME (toFixed(3), zéro final
    // gardé pour l'alignement de colonne). Au centième, 30 % des tirages
    // affichaient « 3,14 » sur les 4 lignes → le distracteur « exactement
    // 3,14 » devenait défendable à la lecture du tableau. Redraws de sécurité :
    // 4 diamètres distincts (sinon 2 lignes rigoureusement jumelles — P est
    // déterministe) et jamais 4 quotients affichés identiques (d = 5, 15, 20,
    // 25 donnent tous « 3,140 »).
    let rows;
    let safety = 0;
    do {
        const selection = randomChoiceMultiple(OBJETS, 4);
        rows = selection.map(obj => {
            const d = randomChoice(obj.diams);
            const p = mesurePerimetre(d);
            const q = (p / d).toFixed(3).replace('.', ',');
            return { nom: obj.nom, d, p, q };
        });
        safety++;
    } while (safety < 60 && (
        new Set(rows.map(r => r.d)).size < 4
        || new Set(rows.map(r => r.q)).size === 1
    ));

    const tableHtml = '<table><tr><th>Objet</th><th>Diamètre D (cm)</th><th>Périmètre P (cm)</th><th>P ÷ D (arrondi au millième)</th></tr>'
        + rows.map(r =>
            `<tr><td>${r.nom}</td><td>${String(r.d).replace('.', ',')}</td><td>${String(r.p).replace('.', ',')}</td><td>${r.q}</td></tr>`
        ).join('')
        + '</table>';

    const texte = `<p>On mesure le périmètre P et le diamètre D de plusieurs objets ronds. On obtient le tableau suivant :</p>${tableHtml}<p>Que peut-on dire du rapport P ÷ D ?</p>`;

    const correct = 'Il est à peu près le même pour tous les objets, environ 3,14.';
    // « Il est différent pour chaque objet » supprimé (revue 2026-07-31) : au
    // millième les 4 valeurs affichées SONT presque toujours distinctes — le
    // distracteur devenait vrai à la lecture. Remplacé par le « aucun lien »,
    // réfuté par n'importe quel tableau (valeurs toutes voisines de 3,14).
    const choix = [
        correct,
        'Il vaut exactement 3,14 pour tous les objets.',
        'Il vaut exactement 3 pour tous les objets.',
        'Il n\'y a aucun lien entre P et D : le rapport change complètement d\'un objet à l\'autre.',
    ];

    const payload = makeQcm(choix, correct, texte, opts(L_PROP));
    payload._meta.generatorKey = 'proportionnalite_pd';
    return payload;
};

const genPropSaisie = () => {
    const obj = randomChoice(OBJETS);
    const d = randomChoice(obj.diams);
    const p = mesurePerimetre(d);
    const dStr = String(d).replace('.', ',');
    const pStr = String(p).replace('.', ',');

    const article = obj.genre === 'f' ? 'Une' : 'Un';
    const texte = `<p>${article} ${obj.nom.toLowerCase()} a un diamètre D = ${dStr} cm et un périmètre P = ${pStr} cm. Calcule P ÷ D (c'est-à-dire P divisé par D). Arrondis le résultat au centième.</p>`;

    const bonneReponse = String(quotientArrondi(p, d)).replace('.', ',');

    const payload = makeSaisie(bonneReponse, texte, opts(L_PROP));
    // Calculatrice AUTORISÉE (revue 2026-07-31) : division décimale au centième
    // (ex. 37,7 ÷ 12) — l'attendu vise le constat de proportionnalité, pas la
    // technique opératoire. Posé dans `contenu` (survit au POST), par FORMAT.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'proportionnalite_pd';
    return payload;
};

const genPropVF = () => {
    const vraies = randomChoiceMultiple(AFFIRMATIONS_VRAIES, 2);
    const fausses = randomChoiceMultiple(AFFIRMATIONS_FAUSSES, 2);

    const lignes = shuffle([...vraies, ...fausses]);
    const bonnesReponses = lignes.map(l => vraies.includes(l) ? 0 : 1);

    const texte = '<p>Indique si chaque affirmation est vraie ou fausse.</p>{{element:0}}';

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_PROP));
    payload._meta.generatorKey = 'proportionnalite_pd';
    return payload;
};

const genPropDrag = () => {
    const generator = randomChoice(PHRASES_TROU_GEN);
    const { phrase, distracteurs } = generator();
    const texte = '<p>Complète la phrase suivante avec les mots proposés.</p>';

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PROP), distracteurs });
    payload._meta.generatorKey = 'proportionnalite_pd';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    proportionnalite_pd_qcm_unique: genPropQcm,
    proportionnalite_pd_texte: genPropSaisie,
    proportionnalite_pd_tableau_vf: genPropVF,
    proportionnalite_pd_texte_trou_drag: genPropDrag,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'proportionnalite_pd', label: L_PROP, formatsList: buildFormats('proportionnalite_pd') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
